/**
 * Heuristics for purchase price from comparable properties.
 * Keep comp-based logic here so SMB surfaces stay aligned as models improve.
 */

export type CompLikeWithLastSale = {
  lastSalePrice?: number | null | undefined;
  sqft?: number | null | undefined;
};

type SubjectPropertyLike = {
  sqft?: number | null | undefined;
};

/** Mean of non-null last sale prices in the given list (e.g. user’s top-three comps). */
export function averageLastSalePriceFromComps(
  comps: readonly CompLikeWithLastSale[],
  subjectProperty: SubjectPropertyLike,
): number | undefined {
  if (comps.some((comp) => !comp.sqft) || !subjectProperty.sqft) {
    // if sqft is missing on any provided property, do non-size-aware calculation
    let sum = 0;
    let count = 0;
    for (const comp of comps) {
      const price = comp.lastSalePrice;
      if (price == null) continue;
      sum += price;
      count += 1;
    }
    if (count === 0) return undefined;
    return sum / count;
  }

  let pricePerSqftSum = 0;
  let count = 0;
  for (const comp of comps) {
    const price = comp.lastSalePrice;
    const sqft = comp.sqft;
    if (price == null || sqft == null) continue;
    pricePerSqftSum += price / sqft;
    count += 1;
  }
  if (count === 0) return undefined;
  return (pricePerSqftSum / count) * subjectProperty.sqft;
}

type PropertyLikeWithEstimate = SubjectPropertyLike & {
  priceEstimate?: number | null | undefined;
};

/**
 * Numeric price estimate for a property: the explicit `priceEstimate` if set,
 * otherwise the average last sale price of the given (selected) comps. Mirrors
 * the source precedence of `resolvePriceEstimate` but returns a number so
 * callers can do arithmetic (e.g. deriving a slider ceiling).
 */
export function resolvePriceEstimateNumber(
  property: PropertyLikeWithEstimate,
  comps: readonly CompLikeWithLastSale[],
): number | undefined {
  if (property.priceEstimate != null) return property.priceEstimate;
  return averageLastSalePriceFromComps(comps, property);
}
